// The yardstick, in words — what a motion needs to carry, said on the paper
// BEFORE the vote is cast rather than beside the result afterwards.
//
// TWO TABLES, BOTH KEYED ON THE MAJORITY BASE AND BOTH IN THIS FILE: what the
// motion NEEDS (`THRESHOLD_COPY`, above the rows, via <Yardstick>) and what an
// ABSTENTION does to that count (`ABSTENTION_NOTE`, beneath them). They are
// neighbours because they are two halves of one explanation of one denominator,
// and a member who met them saying different things would have no way to tell
// which was right. Plus `isMajorityBase`, the narrowing that keeps a base this
// build cannot read off the paper altogether.
//
// One sentence per majority base, written once and reused. The three bases are
// genuinely different denominators (see @cca/decides-count's MajorityBase), and
// the difference decides who governs: under `summoned-per-house` a member who
// does not vote has cast a No, which is the single most misunderstood rule in
// the Constitution and the reason this copy exists at all.

import type { MajorityBase } from '@cca/decides-count';
import { ABSTENTION_IS_A_CHOICE } from './paper';

/** One yardstick sentence. `emphasis` is the clause that must not be skimmed —
 *  rendered in the same sentence, in bold, never as a separate footnote. */
export interface ThresholdCopy {
  sentence: string;
  emphasis?: string;
}

/**
 * Keyed by the ENGINE's own union, so the compiler reds this file the day a
 * fourth base is added upstream — a base with no sentence would otherwise render
 * a blank yardstick, which is the failure that looks fine.
 *
 * KNOWN LIMIT, stated beside the copy rather than discovered later: the wording
 * is keyed on the BASE alone, while the fraction lives in a separate
 * `MotionItem.threshold` field. "Two-thirds" and "Three-quarters" below are true
 * of every use of those bases in the Constitution today (§15.2 and §18.1 are
 * both two-thirds of the members summoned; §9.7/§19.1 is three-quarters of all
 * members), so the two agree in practice — but nothing MAKES them agree. A
 * registrar who set `summoned-per-house` with a three-quarters threshold would
 * get a paper that understates what the motion needs. Deriving the words from
 * the fraction would close that gap and is deliberately not done here: this is
 * registrar-ratified wording, and changing it is their decision, not a
 * builder's. Until then the count itself is unaffected — the engine reads the
 * fraction, never this table.
 */
export const THRESHOLD_COPY: Record<MajorityBase, ThresholdCopy> = {
  'present-and-voting': {
    sentence: 'A simple majority of those who vote. Abstentions are not counted either way.',
  },
  'summoned-per-house': {
    sentence: 'Two-thirds of the members summoned to each House, counted separately.',
    emphasis: 'Not voting counts as a No.',
  },
  'all-synod-members': {
    sentence: 'Three-quarters of every member of Synod, whether present or not.',
  },
};

/**
 * Is this the wire's word for a majority base?
 *
 * `ballot_item.majority_base` arrives as `string | null` — NULL is a POSITION,
 * and the three words are fenced by a CHECK — so this is the narrowing that lets
 * {@link THRESHOLD_COPY} be read with the wire's value without an assertion. It
 * is the motion's `isElectorate` (./paper), written the same way and for the
 * same reason.
 *
 * **THERE IS NO DEFAULT HERE AND THERE MUST NEVER BE ONE.** A motion whose base
 * this build cannot read is REFUSED by the cast screen, never rendered against a
 * substituted one: `present-and-voting` is the lowest of the three
 * constitutional bars, so a `?? 'present-and-voting'` would print _"A simple
 * majority of those who vote"_ on a two-thirds amendment — wrong in the one
 * direction that reassures, on the paper a member consents from. That idiom
 * existed once (`count-bundle.ts:170`) and was removed for exactly this reason.
 *
 * `Object.hasOwn` rather than `in`: `in` walks the prototype, so `'constructor'`
 * would pass the guard and then index to a function whose `.sentence` is
 * `undefined` — a blank yardstick, which is the failure that looks fine.
 */
export function isMajorityBase(value: string | null): value is MajorityBase {
  return value !== null && Object.hasOwn(THRESHOLD_COPY, value);
}

/**
 * WHAT AN ABSTENTION DOES TO THE COUNT — beside {@link THRESHOLD_COPY} so the
 * two sentences a member reads about the same base cannot drift apart.
 *
 * Registrar-ratified, 2026-08-31. It is rendered beneath the vote rows on a
 * motion paper, keyed on the base, and it exists because the answer genuinely
 * changes with the denominator: on a base drawn from the ROLL every member
 * summoned is already in the denominator, so declining to vote for it is a vote
 * against it — the single most misunderstood rule in the Constitution, and one a
 * member cannot discover from the row they are about to mark.
 *
 * **THE VALUE IS ALLOWED TO BE `undefined`, AND THAT IS THE POINT.** The table is
 * still keyed on the engine's own union, so a fourth base reds this file rather
 * than rendering nothing silently — but a total `Record<MajorityBase, string>`
 * would force whoever added the base to INVENT a sentence about how a majority
 * counts abstentions, which is a registrar's ruling and not a builder's. Absent
 * is therefore a ratified answer: under `all-synod-members` the paper says
 * nothing, and no words are made up for it here.
 */
export const ABSTENTION_NOTE: Record<MajorityBase, string | undefined> = {
  // The sentence the election paper already carries, read from its one home in
  // ./paper rather than copied — same base, same wording, one spelling.
  'present-and-voting': ABSTENTION_IS_A_CHOICE,
  'summoned-per-house':
    'Under this majority, an abstention has the same effect on the count as a No.',
  // RATIFIED ABSENCE, not an omission. See the note above.
  'all-synod-members': undefined,
};
